import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Lock, Mail, Eye, EyeOff, Loader2, ArrowRight, KeyRound } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('email'); // 'email', 'admin-password', 'producer-password', 'producer-create'
  const [producerToken, setProducerToken] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Check if admin email
    if (email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
      setStep('admin-password');
      setIsLoading(false);
      return;
    }

    // Check producer auth status
    try {
      const response = await axios.post(`${BACKEND_URL}/api/producer/check-auth`, {
        email: email
      });
      
      const data = response.data;
      
      if (!data.exists) {
        setError('Aucun projet trouvé avec cet email. Soumettez d\'abord un projet via le formulaire de contact.');
        setIsLoading(false);
        return;
      }
      
      if (!data.hasToken) {
        setError('Votre espace n\'est pas encore activé. Contactez Second Souffle pour obtenir votre accès.');
        setIsLoading(false);
        return;
      }
      
      setProducerToken(data.token);
      
      if (data.hasPassword) {
        // Producer has password, ask for it
        setStep('producer-password');
      } else {
        // First time, create password
        setStep('producer-create');
      }
    } catch (err) {
      console.error('Error checking auth:', err);
      setError('Erreur lors de la vérification. Réessayez.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminPasswordSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('adminAuth', 'true');
      navigate('/admin');
    } else {
      setError('Mot de passe incorrect');
    }
  };

  const handleProducerPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/producer/login`, {
        email: email,
        password: password
      });
      
      if (response.data.error) {
        setError(response.data.error);
      } else {
        navigate(`/producteur/${response.data.token}`);
      }
    } catch (err) {
      console.error('Error logging in:', err);
      setError('Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/producer/set-password`, {
        email: email,
        password: password
      });
      
      if (response.data.error) {
        setError(response.data.error);
      } else {
        navigate(`/producteur/${response.data.token}`);
      }
    } catch (err) {
      console.error('Error setting password:', err);
      setError('Erreur lors de la création du mot de passe');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setProducerToken('');
  };

  const getTitle = () => {
    switch (step) {
      case 'admin-password': return 'Administration';
      case 'producer-password': return 'Espace Producteur';
      case 'producer-create': return 'Créer votre mot de passe';
      default: return 'Connexion';
    }
  };

  const getSubtitle = () => {
    switch (step) {
      case 'admin-password': return 'Second Souffle Production';
      case 'producer-password': return 'Entrez votre mot de passe';
      case 'producer-create': return 'Première connexion - créez votre mot de passe';
      default: return 'Entrez votre email pour accéder à votre espace';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      <main className="pt-32 pb-20 px-4 flex items-center justify-center min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass rounded-3xl p-8 md:p-10">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/20 flex items-center justify-center">
                {step === 'producer-create' ? (
                  <KeyRound size={32} className="text-[#D4AF37]" />
                ) : (
                  <Lock size={32} className="text-[#D4AF37]" />
                )}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-light text-white text-center mb-2">
              {getTitle()}
            </h1>
            <p className="text-[#71717a] text-center mb-8">
              {getSubtitle()}
            </p>

            {/* Email Step */}
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Votre email"
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                      data-testid="login-email"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full py-4 bg-[#D4AF37] text-[#050505] rounded-xl font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="login-continue-btn"
                >
                  {isLoading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <>
                      Continuer
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <p className="text-[#71717a] text-xs text-center">
                  Pas encore de projet ?{' '}
                  <a href="/contact" className="text-[#D4AF37] hover:underline">
                    Soumettez votre projet
                  </a>
                </p>
              </form>
            )}

            {/* Admin Password Step */}
            {step === 'admin-password' && (
              <form onSubmit={handleAdminPasswordSubmit} className="space-y-6">
                <div>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-[#a1a1aa] cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mot de passe"
                      required
                      autoFocus
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-12 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717a] hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={!password}
                  className="w-full py-4 bg-[#D4AF37] text-[#050505] rounded-xl font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="login-submit-btn"
                >
                  Accéder
                </button>

                <button
                  type="button"
                  onClick={handleBack}
                  className="w-full text-[#71717a] hover:text-white text-sm transition-colors"
                >
                  ← Retour
                </button>
              </form>
            )}

            {/* Producer Password Step */}
            {step === 'producer-password' && (
              <form onSubmit={handleProducerPasswordSubmit} className="space-y-6">
                <div>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-[#a1a1aa] cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Votre mot de passe"
                      required
                      autoFocus
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-12 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                      data-testid="producer-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717a] hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !password}
                  className="w-full py-4 bg-[#D4AF37] text-[#050505] rounded-xl font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="producer-login-btn"
                >
                  {isLoading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    'Accéder à mon espace'
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBack}
                  className="w-full text-[#71717a] hover:text-white text-sm transition-colors"
                >
                  ← Retour
                </button>
              </form>
            )}

            {/* Producer Create Password Step */}
            {step === 'producer-create' && (
              <form onSubmit={handleCreatePasswordSubmit} className="space-y-6">
                <div>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-[#a1a1aa] cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Créer un mot de passe"
                      required
                      autoFocus
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-12 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                      data-testid="create-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717a] hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-[#71717a] text-xs mt-2">Minimum 6 caractères</p>
                </div>

                <div>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirmer le mot de passe"
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                      data-testid="confirm-password"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !password || !confirmPassword}
                  className="w-full py-4 bg-[#D4AF37] text-[#050505] rounded-xl font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="create-password-btn"
                >
                  {isLoading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    'Créer mon compte'
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBack}
                  className="w-full text-[#71717a] hover:text-white text-sm transition-colors"
                >
                  ← Retour
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default LoginPage;
